import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormField, formInputClass } from './index';
import { fetchPhoneCountriesList } from '../../services/phoneService';
import { findCountryByName } from '../../services/geoService';
import {
  formatInternationalPhone,
  parseInternationalPhone,
  sanitizeNationalInput,
  findPhoneCountryByIso,
} from '../../utils/phoneInternational';

/**
 * Teléfono con selector de código de país (API /api/geo/phone-codes).
 */
export default function PhoneCountryInput({
  value = '',
  onChange,
  countryName = '',
  defaultCountryCode = 'AR',
  label = 'Teléfono',
  labelClass,
  error = '',
  tc,
  compact = false,
  className = '',
  placeholder = '11 2345-6789',
}) {
  const nationalCls = `${formInputClass(tc?.inputClass, { compact, fullWidth: true })} min-w-0 flex-1`;
  const dialCls = `${formInputClass(tc?.inputClass, { compact, fullWidth: false })} w-[6.25rem] max-w-[32%] shrink-0 px-1.5 sm:px-2 tabular-nums`;
  const [phoneCountries, setPhoneCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isoCode, setIsoCode] = useState(defaultCountryCode);
  const [national, setNational] = useState('');
  const userPickedDial = useRef(false);
  const lastEmitted = useRef(value);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const list = await fetchPhoneCountriesList();
        if (!cancelled) setPhoneCountries(list);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Error al cargar prefijos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!phoneCountries.length) return;
    const parsed = parseInternationalPhone(value, phoneCountries);
    if (parsed.countryCode) {
      setIsoCode(parsed.countryCode);
      setNational(parsed.national);
    } else if (parsed.national) {
      setNational(parsed.national);
    } else if (!userPickedDial.current) {
      setIsoCode(defaultCountryCode);
      setNational('');
    }
    lastEmitted.current = value;
  }, [value, phoneCountries, defaultCountryCode]);

  useEffect(() => {
    if (!phoneCountries.length || userPickedDial.current) return;
    const fromPais = findCountryByName(
      phoneCountries.map((p) => ({ code: p.code, name: p.name })),
      countryName,
    );
    if (fromPais?.code) {
      setIsoCode(fromPais.code);
    }
  }, [countryName, phoneCountries]);

  const selectedRow = useMemo(
    () => findPhoneCountryByIso(phoneCountries, isoCode),
    [phoneCountries, isoCode],
  );

  const emitChange = (nextIso, nextNational) => {
    const row = findPhoneCountryByIso(phoneCountries, nextIso);
    const formatted = formatInternationalPhone(row?.dialCode, nextNational);
    if (formatted !== lastEmitted.current) {
      lastEmitted.current = formatted;
      onChange?.(formatted);
    }
  };

  const handleDialChange = (e) => {
    const code = e.target.value;
    userPickedDial.current = true;
    setIsoCode(code);
    emitChange(code, national);
  };

  const handleNationalChange = (e) => {
    const next = sanitizeNationalInput(e.target.value);
    setNational(next);
    emitChange(isoCode, next);
  };

  return (
    <FormField label={label} error={error} labelClass={labelClass} className={className}>
      {loadError && (
        <p className="mb-1 text-xs text-amber-600 dark:text-amber-400">{loadError}</p>
      )}
      <div className="flex w-full items-stretch gap-2">
        <select
          className={dialCls}
          value={isoCode}
          onChange={handleDialChange}
          disabled={loading || !phoneCountries.length}
          aria-label={selectedRow ? `Código de país (${selectedRow.name})` : 'Código de país'}
          title={selectedRow?.name || ''}
        >
          {loading && <option value={isoCode}>…</option>}
          {!loading && phoneCountries.map((row) => (
            <option key={row.code} value={row.code}>
              {row.dialCode}
            </option>
          ))}
        </select>
        <input
          type="tel"
          inputMode="tel"
          className={nationalCls}
          value={national}
          onChange={handleNationalChange}
          placeholder={placeholder}
          autoComplete="tel-national"
          disabled={loading}
        />
      </div>
    </FormField>
  );
}
